import React from 'react';
import { Heart, ArrowRight, Users, Target, LineChart, Shield } from 'lucide-react';
import HeroImage from '../../components/HeroImage';

function CustomerRetention() {
  const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScVJ5NP38k3xJuhVYWnZSFm8PKrzA6Dqu7U73X9fdCI5A2PtQ/viewform?usp=header";

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen mx-[4.2mm] mt-[4.2mm]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00ffff]/10 via-transparent to-[#ff0000]/10 animate-gradient rounded-lg z-10"></div>
          <div className="absolute inset-0 rounded-lg overflow-hidden">
            <HeroImage 
              src="https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&q=80&w=1920&h=1080" 
              alt="Team collaboration meeting"
              priority={true}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 rounded-lg z-20"></div>
        </div>

        <div className="relative h-full flex items-center z-30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center mb-8">
              <Heart className="h-16 w-16 text-[#ff0000]" />
            </div>
            <h1 className="text-5xl font-bold text-center text-white mb-6">Customer Retention Solutions</h1>
            <p className="text-xl text-white/80 text-center max-w-3xl mx-auto">
              We help you understand customer preferences, stay on target with industry trends, and develop strategies to build loyalty to your brand.
            </p>
          </div>
        </div>
      </section>

      {/* Why Trust Atlas AI Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white mb-12">Why Trust Atlas AI with Customer Retention Solutions?</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <TrustCard
            icon={<Users className="h-8 w-8 text-[#ff0000]" />}
            title="Deep Understanding of Your Customers"
            description="Our AI analyzes customer behavior patterns, preferences, and feedback to create a comprehensive understanding of what keeps your customers coming back. We turn data into meaningful relationships."
          />
          <TrustCard
            icon={<Target className="h-8 w-8 text-[#ff0000]" />}
            title="Proactive Relationship Building"
            description="Don't wait for customers to reach out. Our AI predicts customer needs and potential issues before they arise, allowing you to maintain strong relationships through proactive engagement."
          />
          <TrustCard
            icon={<LineChart className="h-8 w-8 text-[#ff0000]" />}
            title="Data-Driven Success Stories"
            description="Our track record speaks for itself. Clients using our retention strategies see an average 60% increase in customer lifetime value and 95% satisfaction rates."
          />
          <TrustCard
            icon={<Shield className="h-8 w-8 text-[#ff0000]" />}
            title="Commitment to Long-Term Growth"
            description="We're not just a service provider; we're your partner in growth. Our AI continuously learns and adapts to ensure your retention strategies evolve with your business."
          />
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8">
          <StatBox
            number="95%"
            label="Customer Satisfaction"
            description="Average satisfaction rate across our clients"
          />
          <StatBox
            number="3x"
            label="Retention Rate"
            description="Increase in customer retention for our clients"
          />
          <StatBox
            number="60%"
            label="Revenue Growth"
            description="Average increase in recurring revenue"
          />
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 py-24">
        <div className="white-holographic-card p-12 rounded-2xl text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Ready to Turn Your Customers Into Fans?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Your customers are always looking for a reason to keep coming back. Let's give them one!
          </p>
          <a
            href={GOOGLE_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-[#ff0000] text-white rounded-lg hover:bg-[#00ffff] transition-colors font-semibold inline-flex items-center mx-auto"
          >
            Start Retaining More Customers
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </div>
    </div>
  );
}

function StatBox({ number, label, description }) {
  return (
    <div className="white-holographic-card p-8 rounded-lg text-center">
      <div className="text-4xl font-bold text-[#ff0000] mb-2">{number}</div>
      <h3 className="text-xl font-semibold text-gray-800 mb-2">{label}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}

function TrustCard({ icon, title, description }) {
  return (
    <div className="white-holographic-card p-8 rounded-lg">
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}

export default CustomerRetention;